import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Alert, Box, Button, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import { endSession, hostStreamUrl, nextQuestion, revealQuestion, startSession } from './api';
import { useLiveState } from './useLiveState';
import Leaderboard from './Leaderboard';
import QuestionCounter from './QuestionCounter';
import QuestionOptions from './QuestionOptions';
import QuestionPrompt from './QuestionPrompt';
import SessionAnalytics from './SessionAnalytics';

function HostSession() {
    const { sessionId } = useParams();
    const { state, live, failed } = useLiveState(hostStreamUrl(sessionId));
    const [busy, setBusy] = useState(false);

    // The action buttons all follow the same shape: disable while in flight, and
    // on failure just log — a 409 (someone else already acted) is corrected by
    // the next SSE push a moment later, there's nothing else to do about it.
    const runAction = (action) => async () => {
        setBusy(true);
        try {
            await action(sessionId);
        } catch (error) {
            console.error('Host action failed', error);
        } finally {
            setBusy(false);
        }
    };

    // Built here, not on the server: this page's own origin is the one address
    // the room can definitely reach. Open the host screen on a LAN IP and the QR
    // points at that IP; open it on the public site and it points there.
    const joinUrl = state && `${window.location.origin}/live/${state.joinCode}`;

    // A QR encoding localhost points a phone at itself. The browser cannot
    // discover the laptop's LAN IP, so the only fix is reopening this screen on
    // it — say so here rather than letting the room find out by failing to scan.
    const isLoopback = ['localhost', '127.0.0.1'].includes(window.location.hostname);

    const isLastQuestion = state?.question && state.question.ord === state.questionCount - 1;
    const primaryActionEndsQuiz = state?.status === 'revealed' && isLastQuestion;

    return (
        <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                <Chip
                    label={failed ? 'Connection lost' : live ? 'Live' : state ? 'Reconnecting…' : 'Connecting…'}
                    color={failed ? 'error' : live ? 'success' : 'default'}
                    size="small"
                />
                {state?.quizTitle && <Chip label={state.quizTitle} size="small" variant="outlined" />}
            </Stack>

            {!state && <Typography color="text.secondary">Connecting…</Typography>}

            {state?.status === 'lobby' && (
                <>
                    {isLoopback && (
                        <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                            This screen is open on <strong>localhost</strong>, so the QR below points a
                            phone at itself. Reopen it at <strong>http://&lt;your-lan-ip&gt;:3000/live/host</strong>
                            {' '}on the same WiFi — run <code>ipconfig getifaddr en0</code> to find the IP.
                        </Alert>
                    )}

                    <Typography color="text.secondary" sx={{ mb: 1 }}>Scan to join, or go to</Typography>
                    <Typography sx={{ mb: 3, fontFamily: 'monospace' }}>{joinUrl}</Typography>

                    <Paper variant="outlined" sx={{ display: 'inline-block', p: 3, mb: 3 }}>
                        <QRCodeSVG value={joinUrl} size={280} />
                    </Paper>

                    {/* Read aloud across a room far more often than the QR actually scans. */}
                    <Typography
                        data-testid="join-code"
                        sx={{ fontSize: { xs: '3rem', md: '5rem' }, fontWeight: 800, letterSpacing: '0.2em', lineHeight: 1.1 }}
                    >
                        {state.joinCode}
                    </Typography>

                    <Box sx={{ mt: 4, mb: 4 }}>
                        <Typography data-testid="joined-count" sx={{ fontSize: '4rem', fontWeight: 800, lineHeight: 1 }}>{state.joinedCount}</Typography>
                        <Typography color="text.secondary">
                            {state.joinedCount === 1 ? 'student joined' : 'students joined'}
                        </Typography>
                    </Box>

                    <Button variant="contained" size="large" disabled={busy} onClick={runAction(startSession)}>
                        Start quiz
                    </Button>
                </>
            )}

            {(state?.status === 'showing' || state?.status === 'revealed') && state.question && (
                <>
                    <QuestionCounter ord={state.question.ord} count={state.questionCount} fontSize="1.1rem" />
                    <QuestionPrompt prompt={state.question.prompt} variant="h4" sx={{ mb: 3 }} />

                    <QuestionOptions
                        question={state.question}
                        revealed={state.status === 'revealed'}
                        counts={state.results}
                        sx={{ maxWidth: 480, mx: 'auto', mb: 4 }}
                    />

                    {/* Pushed on every answer, so it moves without the host acting. */}
                    {state.status === 'showing' && (
                        <Typography data-testid="answered-count" color="text.secondary" sx={{ mb: 3, fontSize: '1.15rem' }}>
                            {state.answered} of {state.joinedCount} answered
                        </Typography>
                    )}

                    {/* hostState only attaches this once revealed — no query fires per-answer. */}
                    {state.status === 'revealed' && (
                        <Leaderboard entries={state.leaderboard} sx={{ mb: 4 }} />
                    )}

                    <Stack direction="row" spacing={2} justifyContent="center">
                        {state.status === 'showing' && (
                            <Button variant="contained" size="large" disabled={busy} onClick={runAction(revealQuestion)}>
                                Reveal
                            </Button>
                        )}
                        {state.status === 'revealed' && (
                            <Button variant="contained" size="large" disabled={busy} onClick={runAction(nextQuestion)}>
                                {isLastQuestion ? 'End quiz' : 'Next question'}
                            </Button>
                        )}
                        {!primaryActionEndsQuiz && (
                            <Button variant="outlined" color="error" disabled={busy} onClick={runAction(endSession)}>
                                End quiz
                            </Button>
                        )}
                    </Stack>
                </>
            )}

            {state?.status === 'ended' && (
                <>
                    <Alert severity="info" sx={{ mt: 4, mb: 4 }}>This quiz has ended.</Alert>
                    <Leaderboard entries={state.leaderboard} sx={{ mb: 4 }} />
                    <SessionAnalytics sessionId={sessionId} />
                </>
            )}
        </Container>
    );
}

export default HostSession;
